import { useState } from "react";
import {
  useGetAlphaBscTokensQuery,
  useTrackAlphaBscTokenMutation,
  useRefreshAlphaBscMutation,
  useRefreshAlphaBscLiveMutation,
  useRankAlphaBscGraduationMutation,
  useRankAllAlphaBscGraduationMutation,
  useDiscoverAlphaBscNowMutation,
  useDiscoverAlphaBscByChainMutation,
  useDiscoverAlphaAllChainsMutation,
  useAlphaBscDeepResearchMutation,
  useRemoveAlphaBscTokenMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DeepResearchDialog } from "@/components/domain/DeepResearchDialog";
import { GeminiDiscoverPanel } from "@/components/domain/GeminiDiscoverPanel";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtPrice } from "@/lib/format";
import { toast } from "sonner";

/**
 * AlphaBscCard — Phase 1 data layer for Binance Alpha BSC tokens.
 *
 * Tracks tokens on Binance Alpha that trade via PancakeSwap (BSC)
 * but are NOT on Binance USDT spot. Pulls live data from GeckoTerminal:
 *   - Current USD price
 *   - 24h on-chain volume
 *   - Pool liquidity
 *   - Computed graduation score (0-100)
 *
 * Auto-refreshes every 15min via scheduler. Click "Refresh now" for
 * an immediate pull. Graduation detection runs each refresh: if a
 * tracked symbol appears on Binance USDT spot, status flips to
 * 'graduated' (Phase 3 will auto-sell at this moment).
 *
 * PHASE 1 = DATA ONLY, NO REAL MONEY MOVES.
 */
type ChainKey = "all" | "bsc" | "solana" | "base" | "sui";
const CHAIN_OPTIONS: { key: ChainKey; label: string }[] = [
  { key: "all", label: "All chains" },
  { key: "bsc", label: "BSC" },
  { key: "solana", label: "Solana" },
  { key: "base", label: "Base" },
  { key: "sui", label: "Sui" },
];

export function AlphaBscCard() {
  const [symbol, setSymbol] = useState("");
  const [notes, setNotes] = useState("");
  const [chain, setChain] = useState<ChainKey>("all");
  // Score tiers calibrated to the actual ranker output. Auto-prune
  // already removes anything < 25. Realistic top tokens score ~30-50;
  // anything >=50 is rare and notable. Default ≥30 = "above the
  // auto-prune line" — hides the long-tail of newly-added unranked
  // tokens (score=0 until ranker fires).
  const [minScore, setMinScore] = useState<0 | 30 | 50 | 70>(30);
  // Sort lens. 'profit' = best trade RIGHT NOW (momentum+liquidity+vol).
  // 'likelihood' = best graduation odds (research+volume trend+age).
  // 'newest' = most recently added.
  const [sortBy, setSortBy] = useState<"profit" | "likelihood" | "newest">("profit");
  const tokens = useGetAlphaBscTokensQuery(
    {
      status: "watching",
      chain: chain === "all" ? undefined : chain,
      min_score: minScore,
    },
    { pollingInterval: 30000 },
  );
  const [track, { isLoading: tracking }] = useTrackAlphaBscTokenMutation();
  const [refresh, { isLoading: refreshing }] = useRefreshAlphaBscMutation();
  const [refreshLive, { isLoading: refreshingLive }] = useRefreshAlphaBscLiveMutation();
  const [rankOne, { isLoading: ranking }] = useRankAlphaBscGraduationMutation();
  const [rankAll, { isLoading: rankingAll }] = useRankAllAlphaBscGraduationMutation();
  const [discover, { isLoading: discovering }] = useDiscoverAlphaBscNowMutation();
  const [discoverChain, { isLoading: discoveringChain }] = useDiscoverAlphaBscByChainMutation();
  const [discoverAll, { isLoading: discoveringAll }] = useDiscoverAlphaAllChainsMutation();
  const [research] = useAlphaBscDeepResearchMutation();
  const [researchTokenId, setResearchTokenId] = useState<number | null>(null);
  const [researchSymbol, setResearchSymbol] = useState<string>("");
  const [researchReport, setResearchReport] = useState<any | null>(null);
  const [researchLoading, setResearchLoading] = useState(false);

  async function onResearch(tokenId: number, sym: string) {
    setResearchTokenId(tokenId);
    setResearchSymbol(sym);
    setResearchReport(null);
    setResearchLoading(true);
    try {
      const r: any = await research({ token_id: tokenId, use_cache: true }).unwrap();
      setResearchReport(r);
    } catch (e: any) {
      toast.error(`research failed: ${e?.data?.detail ?? "?"}`);
      setResearchReport({ status: "error", reason: e?.data?.detail ?? "?" });
    } finally {
      setResearchLoading(false);
    }
  }

  async function onRefreshLive() {
    try {
      const r: any = await refreshLive().unwrap();
      const grads = r?.graduations_detected?.length ?? 0;
      toast.success(
        grads > 0
          ? `🎓 ${grads} graduation(s)! refreshed ${r?.refreshed}`
          : `Live refresh OK — ${r?.refreshed}/${r?.total_watched} via DexScreener`
      );
      tokens.refetch();
    } catch (e: any) {
      toast.error(`refresh failed: ${e?.data?.detail ?? "?"}`);
    }
  }
  const [remove] = useRemoveAlphaBscTokenMutation();

  async function onTrack() {
    if (!symbol.trim()) {
      toast.error("Enter symbol or 0x address");
      return;
    }
    try {
      const r: any = await track({
        symbol_or_address: symbol.trim(),
        notes: notes.trim() || undefined,
      }).unwrap();
      if (r.status === "ok") {
        toast.success(`${r.symbol} added @ $${r.current_price_usd}`);
        setSymbol("");
        setNotes("");
      } else {
        toast.error(`failed: ${r.reason}`);
      }
    } catch (e: any) {
      toast.error(`track failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onDiscover() {
    try {
      const r: any = chain === "all"
        ? await discoverAll().unwrap()
        : chain === "bsc"
          ? await discover().unwrap()
          : await discoverChain({ chain }).unwrap();
      // discoverAll returns {chain: {result}}; flatten into one toast
      if (chain === "all") {
        const parts: string[] = [];
        let totalAdded = 0;
        for (const [c, res] of Object.entries(r || {})) {
          const n = (res as any)?.added_count ?? 0;
          totalAdded += n;
          if (n > 0) {
            const names = ((res as any)?.added || []).map((a: any) => a.symbol).join(", ");
            parts.push(`${c}: ${n} (${names})`);
          }
        }
        if (totalAdded > 0) toast.success(`🔭 ${totalAdded} new across chains · ${parts.join(" | ")}`);
        else toast(`No new candidates on any chain`);
      } else {
        const n = r?.added_count ?? 0;
        if (n > 0) {
          const names = (r?.added || []).map((a: any) => a.symbol).join(", ");
          toast.success(`🔭 ${chain}: ${n} new token(s): ${names}`);
        } else {
          const skipped = r?.skipped_breakdown || {};
          const reasons = Object.entries(skipped)
            .filter(([_, v]) => Number(v) > 0)
            .map(([k, v]) => `${k}:${v}`).join(" · ");
          toast(`${chain}: no new candidates · ${reasons || "all filtered"}`);
        }
      }
      tokens.refetch();
    } catch (e: any) {
      toast.error(`discover failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onRankAll() {
    try {
      const r: any = await rankAll().unwrap();
      toast.success(`Ranked ${r?.ranked ?? 0} tokens by graduation likelihood`);
      tokens.refetch();
    } catch (e: any) {
      toast.error(`rank failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onRankOne(id: number, sym: string) {
    try {
      const r: any = await rankOne(id).unwrap();
      toast.success(`${sym}: ${r?.graduation_likelihood_score?.toFixed(0)} likelihood`);
      tokens.refetch();
    } catch (e: any) {
      toast.error(`${sym} rank failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onRefresh() {
    try {
      const r: any = await refresh().unwrap();
      const gradCount = r?.graduations_detected?.length ?? 0;
      toast.success(
        gradCount > 0
          ? `🎓 ${gradCount} GRADUATION(S)! ${r.graduations_detected.map((g:any)=>g.symbol).join(", ")}`
          : `refreshed ${r?.refreshed ?? 0} tokens`
      );
    } catch (e: any) {
      toast.error(`refresh failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onRemove(id: number, sym: string) {
    if (!confirm(`Stop tracking ${sym}?`)) return;
    try {
      await remove(id).unwrap();
      toast.success(`${sym} removed`);
    } catch (e: any) {
      toast.error(`remove failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>Alpha Watch</span>
          <Badge tone="info">BSC + Solana + Base + Sui</Badge>
          <span className="text-xs text-muted font-normal">
            Binance Alpha tokens · BSC trades on-chain, others via Binance Web3 Wallet
          </span>
        </CardTitle>
        <div className="flex items-center gap-1 flex-wrap">
          <div className="inline-flex border border-border rounded overflow-hidden text-xs">
            {CHAIN_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setChain(opt.key)}
                className={`px-2 py-1 ${chain === opt.key ? "bg-accent text-white" : "bg-bg2 hover:bg-bg2/80"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="inline-flex border border-border rounded overflow-hidden text-xs">
            {([70, 50, 30, 0] as const).map(s => (
              <button
                key={s}
                onClick={() => setMinScore(s)}
                className={`px-2 py-1 ${minScore === s ? "bg-accent text-white" : "bg-bg2 hover:bg-bg2/80"}`}
                title={s === 0
                  ? "Show every token incl. fresh / unranked"
                  : `Show tokens with likelihood ≥ ${s}`}
              >
                {s === 0 ? "All scores"
                 : s === 30 ? "≥30 (above prune)"
                 : s === 50 ? "≥50 (meaningful)"
                 : "≥70 (high conviction)"}
              </button>
            ))}
          </div>
          <div className="inline-flex border border-border rounded overflow-hidden text-xs"
               title="Sort by: Profit (best trade RIGHT NOW) · Likelihood (graduation odds) · Newest">
            {([
              ["profit",     "💰 Profit",  "Best buy/sell setup right now — momentum+liquidity+volume"],
              ["likelihood", "🎓 Likelihood", "Best graduation odds"],
              ["newest",     "⌚ Newest",  "Most recently added"],
            ] as const).map(([k, label, desc]) => (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                className={`px-2 py-1 ${sortBy === k ? "bg-accent text-white" : "bg-bg2 hover:bg-bg2/80"}`}
                title={desc}
              >
                {label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={onRefreshLive} disabled={refreshingLive}>
            {refreshingLive ? "Refreshing…" : "↻ Refresh live"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Slow refresh"}
          </Button>
          <Button size="sm" variant="ghost"
                  title={`Pull pre-graduation alpha tokens from Binance Alpha (chain: ${chain})`}
                  onClick={onDiscover}
                  disabled={discovering || discoveringChain || discoveringAll}>
            {(discovering || discoveringChain || discoveringAll) ? "Discovering…" : `🔭 Discover (${chain})`}
          </Button>
          <Button size="sm" variant="primary"
                  title="Re-rank every token by graduation likelihood: research + news + volume trend + days-on-list"
                  onClick={onRankAll} disabled={rankingAll}>
            {rankingAll ? "Ranking…" : "🎓 Rank graduation"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Add token form */}
        <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-muted/10 rounded border">
          <input
            type="text"
            placeholder="Symbol (NEX) or BSC address (0x...)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="flex-1 min-w-[200px] px-2 py-1 text-sm rounded border bg-bg text-fg font-mono"
          />
          <input
            type="text"
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex-1 min-w-[150px] px-2 py-1 text-sm rounded border bg-bg text-fg"
          />
          <Button size="sm" onClick={onTrack} disabled={tracking}>
            {tracking ? "Adding…" : "+ Track"}
          </Button>
        </div>

        {/* Gemini-driven BSC discovery — daily auto-sweep + run-now */}
        <GeminiDiscoverPanel />

        {tokens.isLoading && <Skeleton className="h-32 w-full" />}
        {tokens.data && tokens.data.length === 0 && (
          <div className="text-sm text-muted italic">
            {minScore > 0 ? (
              <>
                No tokens with likelihood ≥{minScore}
                {chain !== "all" ? ` on ${chain}` : ""}.{" "}
                <button
                  className="underline hover:text-fg not-italic"
                  onClick={() => setMinScore(0)}
                >
                  Show all
                </button>{" "}
                or wait for the ranker — it scores every token hourly.
              </>
            ) : (
              <>No tokens watched yet. Add a Binance Alpha token by symbol
              (e.g. NEX) or contract address.</>
            )}
          </div>
        )}
        {tokens.data && tokens.data.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>Symbol</TH>
                <TH>Chain</TH>
                <TH className="text-right"
                    title="Profit potential RIGHT NOW (0-100): momentum + vol/liq + liquidity floor + research verdict. Hover row for component breakdown.">
                  💰 Profit
                </TH>
                <TH className="text-right" title="Graduation likelihood (research + news + volume + trading pattern, 0-100)">
                  🎓 Likelihood
                </TH>
                <TH title="Latest Gemini Pro deep research verdict — hover for thesis. Click the 🔍 to see full report.">
                  Research
                </TH>
                <TH className="text-right" title="Trading-pattern score only (liquidity + volume + momentum, 0-100)">
                  Pattern
                </TH>
                <TH className="text-right">Price USD</TH>
                <TH className="text-right">24h vol</TH>
                <TH className="text-right">24h%</TH>
                <TH className="text-right">1h%</TH>
                <TH className="text-right">Liquidity</TH>
                <TH className="text-right">Txs 24h</TH>
                <TH>Contract</TH>
                <TH></TH>
              </TR>
            </THead>
            <TBody>
              {[...tokens.data].sort((a: any, b: any) => {
                if (sortBy === "profit") {
                  return (Number(b.profit_potential_score ?? 0)
                          - Number(a.profit_potential_score ?? 0));
                }
                if (sortBy === "likelihood") {
                  return (Number(b.graduation_likelihood_score ?? 0)
                          - Number(a.graduation_likelihood_score ?? 0));
                }
                // newest
                return Number(b.id ?? 0) - Number(a.id ?? 0);
              }).map((t: any) => {
                const scoreTone = t.graduation_score >= 70 ? "good"
                                : t.graduation_score >= 40 ? "warn"
                                                              : "muted";
                const lik = Number(t.graduation_likelihood_score ?? 0);
                const likTone = lik >= 70 ? "good" : lik >= 40 ? "warn" : "muted";
                const price = Number(t.price_usd ?? 0);
                const tokChain = (t.chain || "bsc").toLowerCase();
                const chainLabel = tokChain === "solana" ? "SOL"
                                  : tokChain === "base" ? "BASE"
                                  : tokChain === "sui" ? "SUI"
                                  : "BSC";
                const explorerUrl =
                  tokChain === "solana"
                    ? `https://solscan.io/token/${t.contract_address}`
                    : tokChain === "base"
                      ? `https://basescan.org/token/${t.contract_address}`
                      : tokChain === "sui"
                        ? `https://suivision.xyz/coin/${t.contract_address}`
                        : `https://bscscan.com/token/${t.contract_address}`;
                return (
                  <TR key={t.id}>
                    <TD className="font-mono font-semibold"
                        title={t.notes || ""}>
                      {t.symbol}
                      {t.added_by === "gemini_discover" && (
                        <span className="ml-1 text-[10px]" title="Added by Gemini Pro discovery">🤖</span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={
                        tokChain === "bsc" ? "warn"
                        : tokChain === "solana" ? "info"
                        : tokChain === "base" ? "good"
                        : "muted"
                      }>{chainLabel}</Badge>
                    </TD>
                    <TD className="text-right">
                      {(() => {
                        const pp = Number(t.profit_potential_score ?? 0);
                        const breakdown = t.profit_potential_breakdown || {};
                        const tipLines = [
                          `Momentum: ${breakdown.momentum?.toFixed(0) ?? "?"}`,
                          `Liquidity: ${breakdown.liquidity?.toFixed(0) ?? "?"}`,
                          `Volume:    ${breakdown.volume?.toFixed(0) ?? "?"}`,
                          `Research:  ${breakdown.research?.toFixed(0) ?? "?"}`,
                          ...(breakdown.notes || []),
                          `→ ${t.profit_potential_recommendation || "?"}`,
                        ];
                        const ppTone = pp >= 70 ? "good" : pp >= 50 ? "warn" : pp > 0 ? "muted" : "bad";
                        return pp > 0 ? (
                          <Badge tone={ppTone as any} title={tipLines.join("\n")}>
                            💰 {pp.toFixed(0)}
                          </Badge>
                        ) : (
                          <span className="text-muted text-xs"
                                title={tipLines.join("\n")}>—</span>
                        );
                      })()}
                    </TD>
                    <TD className="text-right"
                        title={t.graduation_likelihood_summary || "Not ranked yet — click Rank graduation"}>
                      {lik > 0 ? (
                        <Badge tone={likTone as any}>🎓 {lik.toFixed(0)}</Badge>
                      ) : (
                        <span className="text-muted text-xs">—</span>
                      )}
                    </TD>
                    <TD title={t.latest_research_thesis || "No deep research yet — click 🔍 to run"}>
                      {t.latest_research_verdict ? (
                        <Badge tone={
                          t.latest_research_verdict === "GO" ? "good"
                          : t.latest_research_verdict === "WAIT" ? "warn"
                          : t.latest_research_verdict === "SKIP" ? "bad"
                          : "muted"
                        }>
                          {t.latest_research_verdict} {t.latest_research_confidence}%
                        </Badge>
                      ) : (
                        <span className="text-muted text-xs">—</span>
                      )}
                    </TD>
                    <TD className="text-right">
                      <Badge tone={scoreTone as any}>{Number(t.graduation_score).toFixed(0)}</Badge>
                    </TD>
                    <TD className="text-right tabular-nums font-mono text-xs">
                      {fmtPrice(price)}
                    </TD>
                    <TD className="text-right tabular-nums">
                      ${Number(t.volume_24h_usd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </TD>
                    <TD className={`text-right tabular-nums ${
                      (t.price_change_24h_pct ?? 0) >= 0 ? "text-green-500" : "text-red-500"
                    }`}>
                      {t.price_change_24h_pct == null
                        ? "—"
                        : `${t.price_change_24h_pct >= 0 ? "+" : ""}${Number(t.price_change_24h_pct).toFixed(1)}%`}
                    </TD>
                    <TD className={`text-right tabular-nums text-xs ${
                      (t.price_change_1h_pct ?? 0) >= 0 ? "text-green-500" : "text-red-500"
                    }`}>
                      {t.price_change_1h_pct == null
                        ? "—"
                        : `${t.price_change_1h_pct >= 0 ? "+" : ""}${Number(t.price_change_1h_pct).toFixed(1)}%`}
                    </TD>
                    <TD className="text-right tabular-nums">
                      ${Number(t.liquidity_usd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </TD>
                    <TD className="text-right tabular-nums">{t.transactions_24h ?? "—"}</TD>
                    <TD className="font-mono text-[10px]">
                      <a href={explorerUrl}
                         target="_blank" rel="noopener noreferrer"
                         className="hover:underline text-muted">
                        {t.contract_address.slice(0, 6)}…{t.contract_address.slice(-4)} ↗
                      </a>
                    </TD>
                    <TD>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost"
                                title="Re-rank graduation likelihood (research + news + volume)"
                                onClick={() => onRankOne(t.id, t.symbol)}
                                disabled={ranking}>
                          🎓
                        </Button>
                        <Button size="sm" variant="ghost"
                                title="Deep research — multi-TF + tokenomics + catalyst + Gemini verdict"
                                onClick={() => onResearch(t.id, t.symbol)}>
                          🔍
                        </Button>
                        <Button size="sm" variant="ghost"
                                title="Remove from watchlist"
                                onClick={() => onRemove(t.id, t.symbol)}>
                          ×
                        </Button>
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
      <DeepResearchDialog
        symbol={researchSymbol}
        tokenId={researchTokenId}
        open={researchTokenId !== null}
        onClose={() => { setResearchTokenId(null); setResearchReport(null); }}
        report={researchReport}
        loading={researchLoading}
      />
    </Card>
  );
}
